/**
 * Email-attachment upload helpers for the Contact Edit Modal compose flow.
 *
 * The handlers operate purely on the `emailAttachments` React state setter,
 * so they are extracted verbatim and parameterized on that setter. The modal
 * orchestrator keeps thin wrappers that delegate here, preserving identical
 * callsite signatures (behavior-preserving — #3237).
 */

import type { Dispatch, SetStateAction, ChangeEvent } from 'react';

export interface EmailAttachment {
  id: string;
  filename: string;
  mimeType: string;
  data: string;
  size: number;
  previewUrl?: string;
}

type SetEmailAttachments = Dispatch<SetStateAction<EmailAttachment[]>>;

/**
 * Read selected files (≤25MB each), base64-encode them, build preview URLs for
 * images, and append to the attachment list. Clears the input value when done.
 */
export async function handleEmailFileSelect(
  e: ChangeEvent<HTMLInputElement>,
  setEmailAttachments: SetEmailAttachments,
): Promise<void> {
  const files = e.target.files;
  if (!files) return;
  for (const file of Array.from(files)) {
    if (file.size > 25 * 1024 * 1024) continue;
    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.readAsDataURL(file);
    });
    const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
    setEmailAttachments(prev => [...prev, { id: `att-${Date.now()}-${Math.random()}`, filename: file.name, mimeType: file.type || 'application/octet-stream', data: base64, size: file.size, previewUrl }]);
  }
  e.target.value = '';
}

/** Remove an attachment by id, revoking any image preview object URL first. */
export function removeEmailAttachment(
  id: string,
  setEmailAttachments: SetEmailAttachments,
): void {
  setEmailAttachments(prev => {
    const att = prev.find(a => a.id === id);
    if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl);
    return prev.filter(a => a.id !== id);
  });
}
